import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getOrg, getOrgApps, getOrgSidecars, OrgSidecar } from '../../api/org';
import { getUserOrg } from '../../api/user';
import { getOrgId, isOrgPath } from '../../hooks/useRoute';
import { DabsiApp } from '../../types/App';
import { Org } from '../../types/Org';
import { UserOrg } from '../../types/User';

type ContextProps = {
  org: Org | null;
  userOrg: UserOrg | null;
  orgApps: DabsiApp[];
  orgAppsLoaded: boolean;
  orgSidecars: OrgSidecar[];
  orgSidecarsLoaded: boolean;
};

const defaultContext: ContextProps = {
  org: null,
  userOrg: null,
  orgApps: [],
  orgAppsLoaded: false,
  orgSidecars: [],
  orgSidecarsLoaded: false,
};

const OrgContext = createContext(defaultContext);

export const OrgProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [orgId, setOrgId] = useState<number | null>(null);
  const [org, setOrg] = useState<Org | null>(null);
  const [userOrg, setUserOrg] = useState<UserOrg | null>(null);
  const [orgApps, setOrgApps] = useState<DabsiApp[]>([]);
  const [orgAppsLoaded, setOrgAppsLoaded] = useState(false);
  const [orgSidecars, setOrgSidecars] = useState<OrgSidecar[]>([]);
  const [orgSidecarsLoaded, setOrgSidecarsLoaded] = useState(false);

  useEffect(() => {
    if (location && isOrgPath()) {
      const orgIdFromPath = getOrgId(location.pathname);
      if (orgIdFromPath == null) {
        setOrgId(null);
        setOrg(null);
        setUserOrg(null);
        setOrgApps([]);
        setOrgAppsLoaded(false);
        setOrgSidecars([]);
        setOrgSidecarsLoaded(false);
        return;
      }

      const nextOrgId = Number(orgIdFromPath);
      if (Number.isNaN(nextOrgId)) {
        console.error('Invalid org ID from path:', orgIdFromPath);
        setOrgId(null);
        setOrg(null);
        setUserOrg(null);
        setOrgApps([]);
        setOrgAppsLoaded(false);
        setOrgSidecars([]);
        setOrgSidecarsLoaded(false);
        return;
      }

      if (orgId !== nextOrgId) {
        setOrgId(nextOrgId);
        setOrg(null);
        setUserOrg(null);
        setOrgApps([]);
        setOrgAppsLoaded(false);
        setOrgSidecars([]);
        setOrgSidecarsLoaded(false);
      }
    } else {
      setOrgId(null);
      setOrg(null);
      setUserOrg(null);
      setOrgApps([]);
      setOrgAppsLoaded(false);
      setOrgSidecars([]);
      setOrgSidecarsLoaded(false);
    }
  }, [location, orgId]);

  const fetchOrgDetails = useCallback(async () => {
    if (!orgId) return null;
    try {
      const org = await getOrg(orgId);
      setOrg(org);
      return org;
    } catch (error) {
      console.error('Failed to fetch org details:', error);
      return null;
    }
  }, [orgId, setOrg]);

  const fetchUserOrg = useCallback(async () => {
    if (!orgId) return null;
    try {
      const userOrg = await getUserOrg(orgId);
      setUserOrg(userOrg);
      return userOrg;
    } catch (error) {
      console.error('Failed to fetch user org:', error);
      return null;
    }
  }, [orgId, setUserOrg]);

  const fetchOrgApps = useCallback(async () => {
    if (!orgId) return;
    try {
      const apps = await getOrgApps(orgId);
      setOrgApps(apps);
    } catch (error) {
      console.error('Failed to fetch org apps:', error);
      setOrgApps([]);
    } finally {
      setOrgAppsLoaded(true);
    }
  }, [orgId]);

  const fetchOrgSidecars = useCallback(async () => {
    if (!orgId) return;
    try {
      const sidecars = await getOrgSidecars(orgId);
      setOrgSidecars(sidecars);
    } catch (error) {
      // Sidecars are an additive feature — failing to fetch them must
      // never break the rest of the org-scoped UI. Log and treat as
      // "no sidecars enabled" so gated UI hides cleanly.
      console.error('Failed to fetch org sidecars:', error);
      setOrgSidecars([]);
    } finally {
      setOrgSidecarsLoaded(true);
    }
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    // Fire all four fetchers concurrently. Each fetcher already
    // swallows its own errors and sets state independently — the
    // four surfaces (org / userOrg / orgApps / orgSidecars) are
    // partially-loadable, so a failed sidecars probe must not hide
    // the rest of the org UI. Wrapping the fan-out in `Promise.all`
    // makes the concurrency intent explicit (no future maintainer
    // can accidentally serialize them by adding an `await`) without
    // changing the per-surface error semantics: each fetcher already
    // resolves on failure, so `Promise.all` here only collects the
    // "all four loaded" signal and never rejects.
    Promise.all([
      fetchOrgDetails(),
      fetchUserOrg(),
      fetchOrgApps(),
      fetchOrgSidecars(),
    ]);
  }, [orgId, fetchOrgDetails, fetchUserOrg, fetchOrgApps, fetchOrgSidecars]);

  const value = useMemo(
    () => ({
      org,
      userOrg,
      orgApps,
      orgAppsLoaded,
      orgSidecars,
      orgSidecarsLoaded,
    }),
    [org, userOrg, orgApps, orgAppsLoaded, orgSidecars, orgSidecarsLoaded]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
};

export const useOrgContext = () => useContext(OrgContext);
